import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { cn } from '../../utils/cn';
import { colorForRetailer, parseDay } from '../production/chartTheme';

/** "2026-08-27" → "8/27/2026" (the Tableau column header format). */
export function formatSpotDate(isoDay) {
  const date = parseDay(isoDay);
  if (!date) return '—';
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
}

/**
 * Dot matrix: one column per spot (newest left), one row per product that
 * shared any of those spots; a dot means "this product was in this spot",
 * colored by the spot's retailer.
 *
 * A popular product over 4 years can mean hundreds of columns × dozens of rows,
 * so hover/click are delegated on the table (one handler, `data-*` lookups)
 * instead of thousands of per-cell handlers, and the table itself is memoized so
 * moving the mouse only re-renders the tooltip.
 *
 * The tooltip is positioned relative to the grid container, not `fixed`: the
 * Modal panel has a CSS transform, which makes `fixed` children resolve against
 * the panel instead of the viewport.
 */
export default function SpotHistoryGrid({
  columns,
  rows,
  selectedSpotId,
  onSelectSpot,
  usedUpcs,
  onCopyUpc,
  onRequestReEnable,
}) {
  const [hover, setHover] = useState(null);
  const containerRef = useRef(null);

  const spotsById = useMemo(() => new Map(columns.map((c) => [c.spotId, c])), [columns]);

  const readTarget = (event) => {
    const el = event.target.closest?.('[data-spot]');
    if (!el) return null;
    return {
      spotId: Number(el.dataset.spot),
      rowIndex: el.dataset.row == null ? null : Number(el.dataset.row),
    };
  };

  const handleMouseOver = useCallback((event) => {
    const target = readTarget(event);
    if (!target) {
      setHover(null);
      return;
    }
    const rect = event.target.closest('[data-spot]').getBoundingClientRect();
    const box = containerRef.current.getBoundingClientRect();
    setHover({
      ...target,
      x: rect.left + rect.width / 2 - box.left,
      y: rect.bottom - box.top,
      width: box.width,
      height: box.height,
    });
  }, []);

  // Same rules as a row in ProductsManager: clicking a product's description
  // copies its UPC and marks it used; a used product can't be copied again until
  // it's re-enabled with a double-click. Clicking a dot or column header opens
  // the spot.
  const handleClick = useCallback((event) => {
    const upcCell = event.target.closest?.('[data-upc]');
    if (upcCell) {
      const { upc } = upcCell.dataset;
      if (!usedUpcs?.has(upc)) onCopyUpc?.(upc);
      return;
    }
    const target = readTarget(event);
    if (target) onSelectSpot(target.spotId);
  }, [onSelectSpot, onCopyUpc, usedUpcs]);

  const handleDoubleClick = useCallback((event) => {
    const upcCell = event.target.closest?.('[data-upc]');
    if (!upcCell || !usedUpcs?.has(upcCell.dataset.upc)) return;
    event.preventDefault();
    const row = rows.find((r) => r.upc === upcCell.dataset.upc);
    if (row) onRequestReEnable?.(row);
  }, [rows, usedUpcs, onRequestReEnable]);

  const hoverSpot = hover ? spotsById.get(hover.spotId) : null;
  const hoverRow = hover?.rowIndex != null ? rows[hover.rowIndex] : null;

  return (
    <div ref={containerRef} className="relative flex-1 min-h-[260px] lg:min-h-0">
      <div
        className="absolute inset-0 overflow-auto border border-neutral-200 rounded-lg"
        onMouseOver={handleMouseOver}
        onMouseLeave={() => setHover(null)}
        onScroll={() => setHover(null)}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      >
        <GridTable columns={columns} rows={rows} selectedSpotId={selectedSpotId} usedUpcs={usedUpcs} />
      </div>

      {hoverSpot && (
        <SpotTooltip spot={hoverSpot} row={hoverRow} {...hover} />
      )}
    </div>
  );
}

const GridTable = memo(function GridTable({ columns, rows, selectedSpotId, usedUpcs }) {
  return (
    <table className="border-separate border-spacing-0 text-xs">
      <thead>
        <tr>
          <th
            scope="col"
            className="sticky top-0 left-0 z-30 bg-white border-b border-r border-neutral-200 w-40 min-w-40 sm:w-72 sm:min-w-72 px-3 py-2 text-left align-bottom font-semibold text-neutral-700"
          >
            Description
          </th>
          {columns.map((spot) => {
            const selected = spot.spotId === selectedSpotId;
            return (
              <th
                key={spot.spotId}
                scope="col"
                className={cn(
                  'sticky top-0 z-20 border-b border-r border-neutral-200 p-0 align-bottom',
                  selected ? 'bg-primary-100' : 'bg-white'
                )}
              >
                <button
                  type="button"
                  data-spot={spot.spotId}
                  aria-pressed={selected}
                  aria-label={`Spot ${spot.spotId}, ${spot.customername}, ${formatSpotDate(spot.startdate)}`}
                  className="w-10 h-36 flex flex-col items-center justify-end gap-2 pb-2 hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
                >
                  <span className="[writing-mode:vertical-rl] rotate-180 font-semibold text-neutral-800 whitespace-nowrap">
                    {formatSpotDate(spot.startdate)}
                  </span>
                  <span className="[writing-mode:vertical-rl] rotate-180 text-neutral-500 tabular-nums whitespace-nowrap">
                    {spot.spotId}
                  </span>
                </button>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => {
          const isUsed = usedUpcs?.has(row.upc) ?? false;
          return (
          <tr key={row.upc}>
            <th
              scope="row"
              data-upc={row.upc}
              title={isUsed ? 'UPC copied. Double-click to re-enable' : 'Click to copy UPC'}
              className={cn(
                'sticky left-0 z-10 border-b border-r border-neutral-200 w-40 min-w-40 max-w-40 sm:w-72 sm:min-w-72 sm:max-w-72 px-3 py-1.5 text-left font-normal select-none transition-colors',
                isUsed
                  ? 'bg-neutral-100 cursor-not-allowed'
                  : cn('cursor-pointer hover:bg-primary-100', row.isSelected ? 'bg-primary-50' : 'bg-white')
              )}
            >
              <div className={cn('text-neutral-800 leading-snug line-clamp-2', row.isSelected && 'font-semibold', isUsed && 'line-through opacity-60')}>
                {row.description || '(no description)'}
              </div>
              <div className={cn('text-[10px] text-neutral-400 tabular-nums', isUsed && 'opacity-60')}>{row.upc}</div>
            </th>
            {columns.map((spot) => {
              const inSpot = row.spotIds.has(spot.spotId);
              return (
                <td
                  key={spot.spotId}
                  data-spot={spot.spotId}
                  data-row={rowIndex}
                  className={cn(
                    'w-10 h-11 border-b border-r border-neutral-100 text-center cursor-pointer',
                    spot.spotId === selectedSpotId
                      ? 'bg-primary-50'
                      : isUsed ? 'bg-neutral-100'
                        : row.isSelected ? 'bg-primary-50/40' : 'bg-neutral-50/60'
                  )}
                >
                  {inSpot && (
                    <span
                      className={cn('inline-block w-[18px] h-[18px] rounded-full align-middle', isUsed && 'opacity-40')}
                      style={{ backgroundColor: colorForRetailer(spot.customername) }}
                    />
                  )}
                </td>
              );
            })}
          </tr>
          );
        })}
      </tbody>
    </table>
  );
});

/** Hover card. Same look as production's TooltipShell, but with text values. */
function SpotTooltip({ spot, row, x, y, width, height }) {
  // Keep the 240px card inside the grid box; flip above the cell near the bottom.
  const left = Math.min(Math.max(x, 124), width - 124);
  const top = y + 180 > height ? Math.max(0, y - 44 - 180) : y;
  const inSpot = row ? row.spotIds.has(spot.spotId) : null;

  return (
    <div
      className="absolute z-40 -translate-x-1/2 mt-1 w-60 bg-white border border-neutral-200 rounded-lg shadow-md px-3 py-2 text-xs pointer-events-none"
      style={{ left, top }}
    >
      <div className="flex items-center gap-1.5 font-semibold text-neutral-900">
        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: colorForRetailer(spot.customername) }} />
        {spot.customername} · {formatSpotDate(spot.startdate)}
      </div>
      <div className="text-neutral-500 mt-0.5 tabular-nums">Spot {spot.spotId}</div>
      <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5">
        <dt className="text-neutral-500">Page</dt><dd className="text-neutral-900">{spot.pagenumber ?? '—'}</dd>
        <dt className="text-neutral-500">Promo</dt><dd className="text-neutral-900">{spot.promotype || '—'}</dd>
        <dt className="text-neutral-500">Ad type</dt><dd className="text-neutral-900">{spot.adtype || '—'}</dd>
      </dl>
      {row && (
        <div className="mt-2 pt-2 border-t border-neutral-200 text-neutral-700">
          <span className="font-medium">{row.description || row.upc}</span>
          {inSpot ? ' was in this spot.' : ' was not in this spot.'}
        </div>
      )}
      <div className="mt-1.5 text-[10px] text-neutral-400">Click for image and SW1 link</div>
    </div>
  );
}
